import React, { useMemo } from 'react';
import Scout from './Scout';
import { rp, AG, STG, OPEN, SUP } from './data';

// Ported from the `s.view==='overview'` block of the original renderVals().
export default function OverviewView({ state, actions, helpers }) {
  const { totals, poTotal } = helpers;

  const vm = useMemo(() => {
    const s = state;
    const drafts = s.pos.filter((p) => p.status === 'draft');
    const openEx = s.exc.filter((e) => e.status === 'open');
    const low = s.inv.filter((i) => i.onHand - i.reserved <= i.rop);
    const human = s.convs.filter((x) => x.status === 'human');

    const gmv = s.orders.reduce((a, o) => a + totals(s, o).total, 0);
    const toFul = s.orders.filter((o) => ['paid', 'picking', 'packed', 'verified'].includes(o.stage)).length;
    const openV = s.pos.filter((p) => OPEN.includes(p.status)).reduce((a, p) => a + poTotal(s, p), 0);

    const crew = Object.entries(AG).map(([k, v]) => ({ key: k, name: v.name, color: v.color }));
    const kpis = [
      { label: 'Orders today', value: s.orders.length, sub: `${rp(gmv)} GMV`, color: '#F2F2F4' },
      { label: 'To fulfil', value: toFul, sub: 'paid → QC passed', color: '#F2F2F4' },
      { label: 'At reorder point', value: low.length, sub: `of ${s.inv.length} fresh SKUs`, color: low.length ? '#F7C66A' : '#5BE3A0' },
      { label: 'Open POs', value: rp(openV), sub: `${s.pos.filter((p) => OPEN.includes(p.status)).length} POs · ${drafts.length} need approval`, color: '#F2F2F4' },
      { label: 'Leakage recovered', value: rp(s.leak), sub: 'this month · four-way match', color: '#6F97FF' },
    ];

    const maxC = Math.max(1, ...STG.map((st) => s.orders.filter((o) => o.stage === st.k).length));
    const pipeline = STG.map((st) => {
      const n = s.orders.filter((o) => o.stage === st.k).length;
      return { label: st.l, count: n, h: 18 + (n / maxC) * 70 + 'px', bg: n === maxC ? '#2F6BFF' : '#1C1C22', onClick: () => actions.setView('orders') };
    });

    const approvals = [
      ...openEx.map((e) => ({
        key: 'tara', title: `Short-ship on ${e.po} · −${e.gap} kg`,
        sub: `${SUP[e.sup].name} delivered ${e.received} of ${e.ordered} kg ${helpers.item(s, e.sku).name.split(' · ')[0]}. ${rp(e.value)} at risk.`,
        primary: `Claim ${rp(e.value)}`, onPrimary: () => actions.resolveEx(e.id, 'claimed'),
        hasSecondary: true, secondary: 'Ask re-delivery', onSecondary: () => actions.resolveEx(e.id, 'redelivery'),
      })),
      ...drafts.map((p) => ({
        key: 'tara', title: `${p.id} · ${SUP[p.sup].name} · ${rp(poTotal(s, p))}`,
        sub: `${helpers.itemsStr(s, p.items)} — ${p.why}`,
        primary: 'Approve', onPrimary: () => actions.approvePO(p.id),
        hasSecondary: true, secondary: 'Reject', onSecondary: () => actions.rejectPO(p.id),
      })),
      ...human.map((x) => ({
        key: x.agent, title: `${x.name} · handed off by ${AG[x.agent].name}`,
        sub: [...x.messages].reverse().find((m) => m.type === 'customer')?.text || '',
        primary: 'Open chat', onPrimary: () => actions.patch({ view: 'inbox', activeId: x.id }), hasSecondary: false,
      })),
    ];

    const feedRows = s.feed.map((f) => ({ key: f.agent, color: AG[f.agent].color, agent: AG[f.agent].name, sys: f.sys, text: f.text, time: f.time }));

    return { kpis, crew, pipeline, approvals, feedRows, feedCount: `${s.feed.length} EVENTS` };
  }, [state, actions, helpers, totals, poTotal]);

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px 140px', background: 'radial-gradient(1200px 520px at 72% -5%, #0C1638 0%, #050506 62%)' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.2em', border: '1px solid #26262E', padding: '5px 11px', borderRadius: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2F6BFF' }} />AGENTIC AI · WHATSAPP LIVE
              </span>
              <span style={{ font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.16em', color: '#6A6A74' }}>CHAT / OMS / WAREHOUSE / PROCUREMENT / DISPATCH</span>
            </div>
            <div style={{ font: "400 54px/1.02 'Instrument Serif',serif", letterSpacing: '-.01em', maxWidth: 900 }}>Total Buah Segar, run by agents.</div>
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: '#9C9CA6', maxWidth: 720 }}>Five agents, one per job, handle customer chats, fresh stock, purchase orders, receiving and delivery across both DCs. Your team only steps in when a decision needs a person.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, flex: 'none' }}>
            {vm.crew.map((cw) => (
              <div key={cw.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <Scout agent={cw.key} style={{ width: 62, height: 64 }} />
                <span style={{ font: "500 9.5px 'Geist Mono',monospace", letterSpacing: '.12em', color: cw.color, textTransform: 'uppercase' }}>{cw.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(0,1fr))', gap: 12 }}>
          {vm.kpis.map((k) => (
            <div key={k.label} style={{ background: '#0A0A0D', border: '1px solid #1C1C22', borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ font: "500 10px 'Geist Mono',monospace", letterSpacing: '.18em', color: '#6A6A74', textTransform: 'uppercase' }}>{k.label}</span>
              <span style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-.02em', color: k.color }}>{k.value}</span>
              <span style={{ fontSize: 12, color: '#9C9CA6' }}>{k.sub}</span>
            </div>
          ))}
        </div>

        <div style={{ background: '#0A0A0D', border: '1px solid #1C1C22', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.18em', color: '#9C9CA6', flex: 1 }}>ORDER STATE</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.14em', color: '#8AAEFF', border: '1px solid #1C2A55', padding: '4px 10px', borderRadius: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2F6BFF' }} />LIVE
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 8, alignItems: 'end' }}>
            {vm.pipeline.map((p) => (
              <button key={p.label} onClick={p.onClick} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch', textAlign: 'center', color: '#F2F2F4' }}>
                <div style={{ height: p.h, borderRadius: 6, background: p.bg, transition: 'height .4s' }} />
                <span style={{ fontSize: 20, fontWeight: 500 }}>{p.count}</span>
                <span style={{ font: "500 10px 'Geist Mono',monospace", letterSpacing: '.14em', color: '#6A6A74', textTransform: 'uppercase' }}>{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)', gap: 16, alignItems: 'start' }}>
          <div style={{ background: '#0A0A0D', border: '1px solid #1C1C22', borderRadius: 14, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1C1C22', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.18em', color: '#9C9CA6', flex: 1 }}>NEEDS A HUMAN</span>
              <span style={{ font: "500 11px 'Geist Mono',monospace", color: '#6A6A74' }}>{vm.approvals.length}</span>
            </div>
            {vm.approvals.map((ap, i) => (
              <div key={i} style={{ padding: '14px 18px', borderBottom: '1px solid #16161B', display: 'grid', gridTemplateColumns: '32px minmax(0,1fr)', gap: 12, alignItems: 'start' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#14141A', border: '1px solid #26262E', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                  <Scout agent={ap.key} head style={{ width: 28, height: 28 }} />
                </div>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{ap.title}</div>
                    <div style={{ fontSize: 12.5, color: '#9C9CA6', lineHeight: 1.45, marginTop: 2 }}>{ap.sub}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={ap.onPrimary} style={{ whiteSpace: 'nowrap', height: 30, padding: '0 12px', borderRadius: 8, border: 0, background: '#2F6BFF', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>{ap.primary}</button>
                    {ap.hasSecondary && (
                      <button onClick={ap.onSecondary} style={{ whiteSpace: 'nowrap', height: 30, padding: '0 12px', borderRadius: 8, border: '1px solid #26262E', background: 'transparent', color: '#F2F2F4', fontSize: 12, cursor: 'pointer' }}>{ap.secondary}</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!vm.approvals.length && <div style={{ padding: '22px 18px', fontSize: 12.5, color: '#6A6A74' }}>Nothing waiting. Every agent is within its limits.</div>}
          </div>

          <div style={{ background: '#0A0A0D', border: '1px solid #1C1C22', borderRadius: 14, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1C1C22', display: 'flex', alignItems: 'center' }}>
              <span style={{ font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.18em', color: '#9C9CA6', flex: 1 }}>AGENT LOOP</span>
              <span style={{ font: "500 10.5px 'Geist Mono',monospace", letterSpacing: '.12em', color: '#6A6A74' }}>{vm.feedCount}</span>
            </div>
            <div style={{ maxHeight: 440, overflow: 'auto' }}>
              {vm.feedRows.map((f, i) => (
                <div key={i} style={{ padding: '10px 18px', borderBottom: '1px solid #16161B', display: 'grid', gridTemplateColumns: '26px minmax(0,1fr) auto', gap: 12, alignItems: 'start' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#14141A', border: '1px solid #26262E', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                    <Scout agent={f.key} head style={{ width: 22, height: 22 }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, lineHeight: 1.45, color: '#E4E4E8' }}>{f.text}</div>
                    <div style={{ font: "500 10px 'Geist Mono',monospace", letterSpacing: '.12em', color: f.color, marginTop: 3, textTransform: 'uppercase' }}>{f.agent} · {f.sys}</div>
                  </div>
                  <span style={{ font: "11px 'Geist Mono',monospace", color: '#6A6A74' }}>{f.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
